// Layout compartido del ticket (entrega y cobro) para ESC/POS y HTML.
// Genera una lista de lineas monospace con su estilo, de modo que ambos renderers
// (escpos.js y TicketImprimir.js) produzcan exactamente el mismo contenido.

export const WIDTH = 42; // columnas en fuente A (80mm). Ancho conservador para no envolver.

export const DEFAULT_TICKET_CONFIG = {
  titulo_entrega: 'TICKET DE ENTREGA',
  titulo_cobro: 'TICKET DE COBRO',
  pie: 'Gracias por su compra',
  moneda_resaltada: null,
  moneda_precios: null,
  subtotal_moneda: 'ambas',
  mostrar_precio_unidad: false,
  // Apariencia
  fuente: 'Consolas',
  tamano: 12,
  interlineado: 1.0,
  negrita: false,
  // Logo
  usar_logo: true,
  logo_url: null,
  mostrar: {
    empresa: true,
    sede: true,
    fecha_hora: true,
    cajero: true,
    cliente: true,
    mesa: true,
    ticket_numero: true,
    pedido_global: true,
    adicionales: true,
    notas: true,
    pie: true,
  },
  notas: [
    { id: 'iva', texto: 'Nuestros precios no incluyen IVA.', activa: true, resaltada: true },
    {
      id: 'seniat',
      texto:
        'No da derecho a credito fiscal. Agradecemos indique datos personales para hacer factura fiscal segun el SENIAT.',
      activa: true,
      resaltada: true,
    },
  ],
  orden: ['cliente', 'notas', 'pie'],
};

// Fuentes permitidas (misma whitelist del backend).
export const FUENTES = [
  'Consolas', 'Courier New', 'DejaVu Sans Mono', 'Lucida Console', 'Monaco', 'Menlo',
  'Arial', 'Verdana', 'Tahoma', 'Segoe UI', 'Roboto', 'Helvetica',
];

const DIACRITICS = {
  'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u', 'ü': 'u', 'ñ': 'n',
  'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U', 'Ü': 'U', 'Ñ': 'N',
};

export function normalize(s) {
  return String(s == null ? '' : s)
    .replace(/[áéíóúüñÁÉÍÓÚÜÑ]/g, (c) => DIACRITICS[c] || c)
    .replace(/·/g, '-')
    .replace(/[^\x20-\x7e]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function mergeConfig(cfg) {
  const src = cfg && typeof cfg === 'object' ? cfg : {};
  const mostrar = { ...DEFAULT_TICKET_CONFIG.mostrar, ...(src.mostrar || {}) };
  const notas = Array.isArray(src.notas) ? src.notas : DEFAULT_TICKET_CONFIG.notas;
  const orden = Array.isArray(src.orden) ? src.orden : DEFAULT_TICKET_CONFIG.orden;
  const fuente = FUENTES.includes(src.fuente) ? src.fuente : DEFAULT_TICKET_CONFIG.fuente;
  return {
    titulo_entrega: src.titulo_entrega || DEFAULT_TICKET_CONFIG.titulo_entrega,
    titulo_cobro: src.titulo_cobro || DEFAULT_TICKET_CONFIG.titulo_cobro,
    pie: typeof src.pie === 'string' ? src.pie : DEFAULT_TICKET_CONFIG.pie,
    moneda_resaltada: src.moneda_resaltada ?? DEFAULT_TICKET_CONFIG.moneda_resaltada,
    moneda_precios: src.moneda_precios ?? DEFAULT_TICKET_CONFIG.moneda_precios,
    subtotal_moneda: ['ambas', 'principal', 'secundaria'].includes(src.subtotal_moneda)
      ? src.subtotal_moneda
      : DEFAULT_TICKET_CONFIG.subtotal_moneda,
    mostrar_precio_unidad: src.mostrar_precio_unidad !== undefined
      ? !!src.mostrar_precio_unidad
      : DEFAULT_TICKET_CONFIG.mostrar_precio_unidad,
    fuente,
    tamano: src.tamano ?? DEFAULT_TICKET_CONFIG.tamano,
    interlineado: src.interlineado ?? DEFAULT_TICKET_CONFIG.interlineado,
    negrita: !!src.negrita,
    usar_logo: src.usar_logo !== false,
    logo_url: src.logo_url ?? null,
    mostrar,
    notas,
    orden,
  };
}

export function fmtMonto(n, simbolo) {
  const v = parseFloat(n);
  const sim = normalize(simbolo || '$');
  if (isNaN(v)) return `${sim}0,00`;
  return `${sim}${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function normalizePedidos(recibo) {
  if (Array.isArray(recibo.pedidos)) return recibo.pedidos;
  return [
    {
      id: recibo.id,
      ticket_numero: recibo.ticket_numero,
      mesa: recibo.mesa,
      cliente: recibo.cliente,
      notas_asesor: recibo.notas_asesor,
      items: recibo.items,
      total: recibo.total,
    },
  ];
}

function monedaPrincipal(monedas) {
  return (
    (Array.isArray(monedas) ? monedas : []).find((m) => m.principal) ||
    (Array.isArray(monedas) ? monedas : [])[0] ||
    null
  );
}

// Moneda "Bolivar" (Bs) para la totalizacion en Bs. Prefiere la moneda con codigo
// VES/VEF/BS; si no existe, usa la primera moneda secundaria (no principal).
function monedaBs(monedas, principal) {
  const arr = Array.isArray(monedas) ? monedas : [];
  return (
    arr.find((m) => ['VES', 'VEF', 'BS'].includes((m.codigo || '').toUpperCase())) ||
    arr.find((m) => m !== principal) ||
    null
  );
}

// Resuelve una moneda por su codigo (case-insensitive).
function monedaPorCodigo(monedas, codigo) {
  if (!codigo) return null;
  const c = String(codigo).toUpperCase();
  return (Array.isArray(monedas) ? monedas : []).find((m) => (m.codigo || '').toUpperCase() === c) || null;
}

// Rellena con espacios entre izquierda y derecha.
export function lr(left, right, width = WIDTH) {
  const l = normalize(left);
  const r = normalize(right);
  const gap = width - l.length - r.length;
  if (gap < 1) return (l + ' ' + r).slice(0, width);
  return l + ' '.repeat(gap) + r;
}

// Rellena con puntos (linea punteada) entre nombre y total.
export function dotted(left, right, width = WIDTH) {
  const l = normalize(left);
  const r = normalize(right);
  const gap = width - l.length - r.length;
  if (gap < 1) return (l + ' ' + r).slice(0, width);
  return l + '.'.repeat(gap) + r;
}

export function dash(width = WIDTH) {
  return '-'.repeat(width);
}

// Divide texto en lineas de ancho maximo `width` (word-wrap; corta palabras largas).
export function wrap(text, width = WIDTH, indent = 0) {
  const t = normalize(text);
  if (!t) return [];
  const avail = width - indent;
  const prefix = ' '.repeat(indent);
  const words = t.split(' ');
  const lines = [];
  let cur = '';
  const push = () => {
    if (cur) {
      lines.push(prefix + cur);
      cur = '';
    }
  };
  for (let w of words) {
    while (w.length > avail) {
      if (cur) push();
      lines.push(prefix + w.slice(0, avail));
      w = w.slice(avail);
    }
    if (!cur) {
      cur = w;
    } else if ((cur + ' ' + w).length <= avail) {
      cur += ' ' + w;
    } else {
      push();
      cur = w;
    }
  }
  push();
  return lines.length ? lines : [''];
}

// Estilos disponibles: normal | center | bold | bold-center | double
function L(text, style = 'normal') {
  return { kind: 'text', text, style };
}

// Par etiqueta/valor (alineado a los extremos). `text` conserva la version con
// padding (para ESC/POS y tests); los renderers HTML usan `left`/`right` para
// llenar el ancho real con cualquier fuente.
function pair(left, right, dotted = false, style = 'normal') {
  const l = normalize(left);
  const r = normalize(right);
  return { kind: 'pair', left: l, right: r, dotted, text: dotted ? dottedText(l, r) : lr(l, r), style };
}

// Divisor de ancho completo.
function divider() {
  return { kind: 'divider', text: dash(), style: 'normal' };
}

// Versión de `dotted` que recibe strings ya normalizados (evita doble normalize).
function dottedText(l, r, width = WIDTH) {
  const gap = width - l.length - r.length;
  if (gap < 1) return (l + ' ' + r).slice(0, width);
  return l + '.'.repeat(gap) + r;
}

// ── Render compartido (HTML de impresión y canvas de vista previa) ──
// La misma fuente/CSS se usa en buildTicketHtml (TicketImprimir) y en TicketCanvas
// (preview), de modo que la vista previa sea identica al papel impreso.
export const TICKET_FONT = "'Courier New', 'DejaVu Sans Mono', monospace";

export const STYLE_CLASS = {
  normal: '',
  center: 'c',
  bold: 'b',
  'bold-center': 'bc',
  double: 'd',
  total: 'total',
};

export const TICKET_CSS = `
.agx-ticket { font-family: ${TICKET_FONT}; font-size: 12px; line-height: 1.0; color: #000; }
.agx-ticket .line { white-space: pre; }
.agx-ticket .logo { text-align: center; margin-bottom: 2mm; }
.agx-ticket .logo img { max-width: 48mm; max-height: 16mm; display: inline-block; }
.agx-ticket .c { text-align: center; }
.agx-ticket .b { font-weight: bold; }
.agx-ticket .bc { font-weight: bold; text-align: center; }
.agx-ticket .d { font-weight: bold; font-size: 20px; text-align: center; }
`;

// Construye el CSS del ticket aplicando fuente/tamano/interlineado/negrita global.
export function ticketCss(cfg) {
  const fuente = cfg.fuente || 'Consolas';
  const tamano = cfg.tamano || 12;
  const interlineado = cfg.interlineado ?? 1.0;
  const negrita = !!cfg.negrita;
  const peso = negrita ? 'bold' : 'normal';
  return `
.agx-ticket { font-family: '${fuente}', monospace; font-size: ${tamano}px; line-height: ${interlineado}; color: #000; font-weight: ${peso}; }
.agx-ticket .line { white-space: pre; }
.agx-ticket .logo { text-align: center; margin-bottom: 1mm; }
.agx-ticket .logo img { max-width: 48mm; max-height: 16mm; display: inline-block; }
.agx-ticket .c { text-align: center; }
.agx-ticket .b { font-weight: bold; }
.agx-ticket .bc { font-weight: bold; text-align: center; }
.agx-ticket .d { font-weight: bold; font-size: ${Math.min(tamano + 8, 30)}px; text-align: center; }
.agx-ticket .divider { border-top: 1px dashed #000; margin: 1px 0; }
.agx-ticket .pair { display: flex; align-items: baseline; }
.agx-ticket .pair .left { white-space: nowrap; }
.agx-ticket .pair .right { white-space: nowrap; }
.agx-ticket .pair .fill { flex: 1 1 auto; min-width: 6px; }
.agx-ticket .pair .fill.dots { border-bottom: 1px dotted #000; transform: translateY(-3px); }
.agx-ticket .pair.b { font-weight: bold; }
.agx-ticket .pair.bc { font-weight: bold; text-align: center; }
.agx-ticket .pair.total { font-weight: bold; font-size: ${Math.min(tamano + 6, 22)}px; }
.agx-ticket .total { font-weight: bold; font-size: ${Math.min(tamano + 6, 22)}px; }
`;
}

export function buildTicketLines(recibo, modo, monedasSel = [], incluirIva) {
  const cfg = mergeConfig(recibo?.ticket_config);
  const m = cfg.mostrar;
  const lines = [];

  const empresa = recibo.empresa || '';
  const sede = recibo.sede || '';
  const cajero = recibo.cajero || '';
  const fecha = recibo.fecha ? new Date(recibo.fecha).toLocaleString() : '';
  const pedidos = normalizePedidos(recibo);
  const monedas = Array.isArray(recibo.monedas) ? recibo.monedas : [];
  const principal = monedaPrincipal(monedas);
  const principalSimbolo = principal?.simbolo || '$';
  const bs = monedaBs(monedas, principal);
  const tasaBs = bs ? parseFloat(bs.tasa) || 1 : null;
  const titulo = modo === 'entrega' ? cfg.titulo_entrega : cfg.titulo_cobro;

  // Moneda de precios configurable: si `moneda_precios` apunta a una moneda de la
  // empresa, todos los precios (items, precio por unidad y totales) se muestran en
  // esa moneda. Si no esta configurada (o es invalida), se usa la moneda principal.
  const monedaPrecio = monedaPorCodigo(monedas, cfg.moneda_precios) || principal;
  const precioSimbolo = monedaPrecio?.simbolo || principalSimbolo;
  const precioTasa =
    monedaPrecio && principal && monedaPrecio !== principal ? parseFloat(monedaPrecio.tasa) || 1 : 1;

  // IVA: se cobra en Bs siempre. El IVA solo aparece si se indica (entrega: ya
  // decidido al cobrar; cuenta: toggle del usuario).
  const subtotal = parseFloat(recibo.subtotal) || 0;
  const impuesto = parseFloat(recibo.impuesto) || 0;
  const impuestoPct = parseFloat(recibo.impuesto_porcentaje) || 0;
  const propina = parseFloat(recibo.propina) || 0;
  const cobrarIva = incluirIva !== undefined ? !!incluirIva : !!recibo.cobrar_impuesto;

  // ── Membrete ──
  if (m.empresa && empresa) lines.push(L(empresa, 'double'));
  if (titulo) lines.push(L(titulo, 'bold-center'));
  if (m.sede && sede) lines.push(L(sede, 'center'));
  if (m.fecha_hora && fecha) lines.push(L(fecha, 'center'));
  if (m.cajero && cajero) lines.push(L(`Cajero: ${cajero}`, 'center'));
  lines.push(divider());

  // ── Cliente (siempre debajo del membrete) ──
  if (m.cliente) {
    for (const p of pedidos) {
      const c = p.cliente;
      if (!c) continue;
      const nombre = c.nombre ? normalize(c.nombre) : '';
      const cedula = c.cedula ? normalize(c.cedula) : '';
      const telefono = c.telefono ? normalize(c.telefono) : '';
      if (nombre) lines.push(L(`Cliente: ${nombre}`));
      if (cedula) lines.push(L(`Cedula: ${cedula}`));
      if (telefono) lines.push(L(`Tel: ${telefono}`));
      break; // un solo bloque de cliente
    }
    lines.push(divider());
  }

  // ── Productos (cabecera CANT / TOTAL) ──
  lines.push(pair('CANT', 'TOTAL'));
  lines.push(divider());

  pedidos.forEach((p, idx) => {
    const cab = [
      m.pedido_global && p.id != null ? `Pedido Nro. ${p.id}` : '',
      m.ticket_numero && p.ticket_numero != null ? `#${p.ticket_numero}` : '',
      m.mesa && p.mesa != null && p.mesa !== '' ? `Mesa ${p.mesa}` : '',
    ]
      .filter(Boolean)
      .join(' - ');
    if (cab) lines.push(L(cab, 'bold'));

    (Array.isArray(p.items) ? p.items : []).forEach((it) => {
      const cant = it.cantidad != null ? it.cantidad : 1;
      const nombre = it.nombre || it.codigo || '';
      const subtotal = parseFloat(it.subtotal) || 0;
      const precioUnidad = parseFloat(it.precio_unitario) || 0;
      lines.push(pair(`${cant}x ${nombre}`, fmtMonto(subtotal * precioTasa, precioSimbolo), true));

      // Precio por unidad (solo si esta habilitado y hay mas de una unidad).
      if (cfg.mostrar_precio_unidad && cant > 1 && precioUnidad > 0) {
        lines.push(L(`   p/u ${fmtMonto(precioUnidad * precioTasa, precioSimbolo)}`));
      }

      (Array.isArray(it.adicionales) ? it.adicionales : []).forEach((a) => {
        if (!m.adicionales) return;
        const extra = a.precio ? ` (${fmtMonto(a.precio * precioTasa, precioSimbolo)})` : '';
        wrap(`+ ${a.nombre || ''}${extra}`, WIDTH, 3).forEach((ln) => lines.push(L(ln)));
      });
      (Array.isArray(it.componentes) ? it.componentes : []).forEach((c) => {
        const etiq = (Array.isArray(c.etiquetas) ? c.etiquetas : [])
          .map((e) => `${e.nombre || ''}${e.precio ? ` +${fmtMonto(e.precio * precioTasa, precioSimbolo)}` : ''}`)
          .join(', ');
        wrap(`- ${c.nombre || ''}${etiq ? ` (${etiq})` : ''}`, WIDTH, 3).forEach((ln) => lines.push(L(ln)));
      });
      if (it.nota) wrap(`* ${it.nota}`, WIDTH, 3).forEach((ln) => lines.push(L(ln)));
    });

    if (p.notas_asesor) wrap(`Notas: ${p.notas_asesor}`, WIDTH, 0).forEach((ln) => lines.push(L(ln)));

    if (pedidos.length > 1 && idx < pedidos.length - 1) lines.push(divider());
  });

  // ── Totales ──
  lines.push(divider());

  // Bloque de totales/subtotal. `subtotal_moneda` controla la moneda del subtotal
  // y el total de forma independiente al precio de los items:
  //   - 'principal'   -> solo en la moneda principal ($).
  //   - 'secundaria'  -> solo en la moneda secundaria (Bs).
  //   - 'ambas'       -> REF ($) + equivalente en Bs (default).
  // `moneda_precios` solo afecta el precio de los items (lineas de productos),
  // no el bloque de totales.
  const modoSubtotal = cfg.subtotal_moneda || 'ambas';
  const totalBase = subtotal + (cobrarIva ? impuesto : 0);
  const totalAPagar = totalBase + propina;
  const diferenciaCobro = parseFloat(recibo.diferencia_cobro) || 0;

  const emitirTotalesMoneda = (simbolo, tasa) => {
    if (cobrarIva && impuesto > 0) {
      lines.push(pair('SUBTOTAL', fmtMonto(subtotal * tasa, simbolo)));
      lines.push(pair(`IVA ${impuestoPct}%`, fmtMonto(impuesto * tasa, simbolo)));
    }
    if (propina > 0) lines.push(pair('PROPINA', fmtMonto(propina * tasa, simbolo)));
    lines.push(pair(propina > 0 ? 'TOTAL A PAGAR' : 'TOTAL', fmtMonto(totalAPagar * tasa, simbolo), false, 'total'));
  };

  const emitirTotalesDobles = () => {
    // Estructura REF/BS: subtotal en dolares (REF), equivalente en Bs, IVA en Bs.
    lines.push(pair('Sub total REF', fmtMonto(subtotal, principalSimbolo)));
    lines.push(pair(`Sub Total ${bs.simbolo || 'Bs'}`, fmtMonto(subtotal * tasaBs, bs.simbolo)));
    if (cobrarIva && impuesto > 0) {
      lines.push(pair(`IVA (${impuestoPct}%)`, fmtMonto(impuesto * tasaBs, bs.simbolo)));
    }
    if (propina > 0) lines.push(pair('PROPINA', fmtMonto(propina * tasaBs, bs.simbolo)));
    lines.push(pair(propina > 0 ? 'TOTAL A PAGAR' : 'TOTAL', fmtMonto(totalAPagar * tasaBs, bs.simbolo), false, 'total'));
  };

  if (modoSubtotal === 'principal') {
    emitirTotalesMoneda(principalSimbolo, 1);
  } else if (modoSubtotal === 'secundaria' && bs && tasaBs) {
    emitirTotalesMoneda(bs.simbolo || 'Bs', tasaBs);
  } else if (bs && tasaBs) {
    // 'ambas' (default): REF ($) + Bs, aun cuando moneda_precios este seteada.
    emitirTotalesDobles();
  } else {
    // Fallback sin moneda secundaria: totalizacion en la moneda principal.
    emitirTotalesMoneda(principalSimbolo, 1);
  }

  // ── Diferencia de pago (falta / sobrante) ──
  if (diferenciaCobro < -0.005) {
    lines.push(pair('FALTA POR PAGAR', fmtMonto(Math.abs(diferenciaCobro), principalSimbolo)));
  } else if (diferenciaCobro > 0.005) {
    lines.push(pair('SOBRANTE (NO PROPINA)', fmtMonto(diferenciaCobro, principalSimbolo)));
  }

  // ── Notas (post-totales) ──
  if (m.notas) {
    const notasActivas = (cfg.notas || []).filter((n) => n && n.activa !== false && (n.texto || '').trim());
    if (notasActivas.length) {
      lines.push(divider());
      notasActivas.forEach((n) => {
        wrap(n.texto, WIDTH, 0).forEach((ln) => lines.push(L(ln, n.resaltada ? 'bold' : 'normal')));
      });
    }
  }

  // ── Pie ──
  if (m.pie && cfg.pie) {
    lines.push(divider());
    lines.push(L(cfg.pie, 'center'));
  }

  return lines;
}
